/// <reference types="../../../../../backend/public/api/generated/types/public" />
import * as Sentry from "@sentry/nextjs";
import { NextPage } from "next";
import { NextRouter, useRouter } from "next/router";
import { NextSeo } from "next-seo";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import ErrorPage from "../../../components/ErrorPage";
import LoadingSpinner from "../../../components/LoadingSpinner";
import PrintableSeasonCard from "../../../components/PrintableSeasonCard";
import SeasonCardMissingInformation from "../../../components/SeasonCardMissingInformation";
import TooManyRequests from "../../../components/TooManyRequests";
import { NEXT_SEO_HIDDEN_CONFIG } from "../../../seo/seo";

import css from "./[id].module.scss";

type SeasonCard = {
  name: string;
  qrCodeURL: string;
  photoURL: string;
  print?: "card";
};

type SeasonCardResponse =
  | {
      seasonCard: SeasonCard;
      status: 200;
    }
  | {
      status: 403 | 404 | 422;
      seasonCard?: never;
    };

const notFound: SeasonCardResponse = {
  status: 404,
};

async function getSeasonCard(
  query?: NextRouter["query"],
): Promise<SeasonCardResponse> {
  try {
    if (query) {
      const { id } = query;
      const baseURL = `${process.env.SARKANNIEMI_API_URI}/v1`;
      const seasonPassResponse = await fetch(
        `${baseURL}/ecommerce/season-pass/${id}`,
      );

      switch (seasonPassResponse.status) {
        case 200: {
          const { firstName, lastName, pictureUrl, ticketMediaId } =
            (await seasonPassResponse.json()) as Paths.GetSeasonPass.Responses.$200;

          // use the media id QR code by default, if a media is associated with the season pass ticket
          const qrCodeURL =
            ticketMediaId !== undefined
              ? `${baseURL}/ticket/code/media:${ticketMediaId}?scale=6`
              : `${baseURL}/ticket/code/ticket:${id}?scale=6`;
          const print = query.print === "card" ? "card" : undefined;

          const seasonCard: SeasonCard = {
            name: `${firstName} ${lastName}`,
            qrCodeURL,
            photoURL: pictureUrl,
            ...(print !== undefined && { print }),
          };

          return {
            status: 200,
            seasonCard,
          };
        }
        case 403: {
          return {
            status: 403,
          };
        }
        case 422: {
          return {
            status: 422,
          };
        }
        default: {
          return notFound;
        }
      }
    }

    return notFound;
  } catch (error) {
    Sentry.captureException(error);
    return notFound;
  }
}

export const SeasonCardPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const { query } = useRouter();
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [seasonCard, setSeasonCard] = useState<SeasonCard | null>(null);

  useEffect(() => {
    getSeasonCard(query).then(({ status, seasonCard }) => {
      if (seasonCard) {
        setSeasonCard(seasonCard);
      }
      setStatusCode(status);
    });
  }, [query]);

  return (
    <div className={css.container}>
      <NextSeo
        {...NEXT_SEO_HIDDEN_CONFIG}
        title={formatMessage({ id: "seasonCardPage.pageTitle" })}
      />
      {statusCode === 200 && seasonCard ? (
        <PrintableSeasonCard
          name={seasonCard.name}
          photoURL={seasonCard.photoURL}
          print={seasonCard.print}
          qrCodeURL={seasonCard.qrCodeURL}
        />
      ) : statusCode === 403 ? (
        <TooManyRequests />
      ) : statusCode === 404 ? (
        <ErrorPage />
      ) : statusCode === 422 && typeof query.id === "string" ? (
        <SeasonCardMissingInformation id={query.id} />
      ) : (
        <LoadingSpinner
          message={formatMessage({ id: "seasonCardPage.loading" })}
        />
      )}
    </div>
  );
};

const SeasonCardPageIndex: NextPage = () => <SeasonCardPage />;

export default SeasonCardPageIndex;
